import {useEffect, useState, useMemo, useRef} from 'react'
import {useTheme} from '@chakra-ui/react'
import {isServer as globalIsServer} from '../utils/utils'

/**
 * Find the current breakpoint value based on the media queries, values, and names.
 * @param {Object} values - the defined values per breakpoint with gaps. Gap means that a value from smaller breakpoint will be used if the current one is not defined.
 * @param {Array<string>} names - the ordered names of the breakpoints, used to match values.
 * @param {Array<MediaQueryList>} mqls - ordered list of media query lists for each breakpoint. IMPORTANT: must match the order of names.
 * @param {boolean} isServer - whether the code is running on the server or client side
 * @returns {*|undefined} - the value of the current breakpoint, or undefined if no matching breakpoint is found. Fallbacks to the first breakpoint value on server side.
 */
const findCurrentBreakpointValue = ({values, names, mqls, isServer}) => {
    if (isServer) {
        // return first breakpoint value on server
        return values[names[0]]
    }

    // iterate through ordered breakpoint names and find the first one that is matched by media query
    // at the same time keep the last seen value from breakpointsRef to return it
    const {found, value} = names.reduce(
        ({value, found}, name, index) => {
            // if we already found a matching breakpoint, we can stop
            if (found) return {value, found}
            // update the value if there's a new one
            if (values[name] !== undefined) {
                value = values[name]
            }
            found = mqls[index].matches
            return {value, found}
        },
        {found: false}
    )

    // not found usually happens in tests, so we return the first value
    return found ? value : values[names[0]]
}

/**
 * Custom hook to get a value based on the current breakpoint. When a value is not defined for a breakpoint,
 * it will use the value from the previous breakpoint (if defined). On server side it will always return the first breakpoint value (`base`).
 *
 * Note: does not support SSR, or dynamic breakpoint values.
 * If you need a dynamic value, use a function as a value and execute it. Example:
 *
 * ```js
 * const someComputedValue = useBreakpoints({
 *   base: () => 'baseValue',
 *   md: () => 'smallValue',
 * })()
 * ```
 *
 * To memoize a complex computation then you need to memoize the function:
 * ```js
 * const calcBase = useCallback(() => 'base', [])
 * const calcMd = useCallback(() => 'small', [])
 * const calcFn = useBreakpoints({
 *   base: calcBase,
 *   md: calcMd,
 * })
 * const computedValue = useMemo(() => calcFn(), [calcFn])
 * ```
 *
 * @param {{
 *   base?: *,
 *   sm?: *,
 *   md?: *,
 *   lg?: *,
 *   xl?: *,
 *   '2xl'?: *
 * }} breakpointValues - an object with breakpoint names as keys and values as values.
 * @param {Object} [options] - options for the hook
 * @param {boolean} [options.isServer] - whether the code is running on the server or client side. Should not be used as it's defined for testing purposes only.
 * @returns {*}
 */
function useBreakpoints(breakpointValues, {isServer = globalIsServer} = {}) {
    // this is internal chakra processed breakpoints value - tests do not mock it, so if this breaks upon upgrade we will know
    const {__breakpoints: {details: detailedBreakpoints} = {}} = useTheme()
    const orderedBreakpointNames = useMemo(
        () => detailedBreakpoints.map(({breakpoint}) => breakpoint),
        [detailedBreakpoints]
    )
    const breakpointsRef = useRef(breakpointValues)
    // don't memoize but put in ref to avoid re-rendering
    // the initialBreakpoints is a new object every time, but values inside do not change
    breakpointsRef.current = breakpointValues

    // keep the compiled mqls in a ref as useMemo can be discarded by react and
    // that will lead to memory leaks from the registered listeners
    const mqlRef = useRef()
    if (!isServer && !mqlRef.current) {
        // update once only on client side
        mqlRef.current = detailedBreakpoints.map(({minMaxQuery}) =>
            window.matchMedia(minMaxQuery.replace('@media screen and ', ''))
        )
    }

    const [currentValue, setCurrentValue] = useState(() =>
        findCurrentBreakpointValue({
            values: breakpointsRef.current,
            names: orderedBreakpointNames,
            mqls: mqlRef.current,
            isServer
        })
    )

    // register a change listener for each media query and update the current value on change
    useEffect(() => {
        if (isServer) return

        const handleChange = () => {
            // internally react will check if the old and new value are the same (using Object.is)
            // and trigger a re-render only if they are different
            setCurrentValue(
                findCurrentBreakpointValue({
                    values: breakpointsRef.current,
                    names: orderedBreakpointNames,
                    mqls: mqlRef.current,
                    isServer
                })
            )
        }

        // take a shallow copy to unregister the same listeners on unmount
        const mqls = [...mqlRef.current]
        // register the change listener for each media query
        mqls.forEach((mql) => mql.addEventListener('change', handleChange))
        // unregister the listeners on unmount
        return () => mqls.forEach((mql) => mql.removeEventListener('change', handleChange))
    }, [orderedBreakpointNames, isServer])

    return currentValue
}

export default useBreakpoints
